import { readNativeAvailMB } from '~/services/logging/crashCapture';
import {
  getBootId,
  isChangeOn,
  noteChangeAction,
} from '~/services/diagnostics/changeSpy';
import { detectPlatform } from '~/services/platform/utils/detectPlatform';
import type { Platform } from '~/services/platform/types';
import { markRecycleBoot, paintBlackNow } from '~/utils/recycleBoot';
import { LAST_RELOAD_AT_KEY } from '~/services/storageKeys';

/**
 * RLD — mitigação DEFINITIVA do leak nativo do AVPlay: recarrega o app periodicamente (2026-07-27).
 *
 * <p>⚠️ PREMISSA ORIGINAL — **REFUTADA EM CAMPO (2026-10-14)**, mantida aqui porque explica por que
 * este código nasceu chamando `location.reload()`: _"PROVADO NA BANCADA (Tizen 9.0):
 * `location.reload()` RENOVA o processo nativo → devolve o CMA. Medido: recarregou em 460MB e o
 * próximo boot voltou pra ~848MB (+388MB de uma vez); daí em diante a RAM fica flat em ~845 a cada
 * reload."_ Aquela medição é de OUTRA bancada (o texto cita ~845MB livres; a consumer de prod
 * UN50U8600FGXZD tem 1.568MB no TOTAL) e **não se confirma na consumer Tizen 9.0**:
 * <ul>
 *   <li>A recarga de página NÃO troca o processo. Prova: `uptime.process_ms` do `health:report`
 *   (derivado do `sessionStorage`, que sobrevive ao reload e só é apagado quando o app sai) mostrou
 *   <b>13,5 dias</b> de processo contínuo atravessando dezenas de recargas, com
 *   `reloaded_in_same_process: true`.</li>
 *   <li>O leak do AVPlay é NATIVO, fora do heap JS: medido na ANL-Player-TV (Tizen 9.0), ~2,8 KB por
 *   abertura de vídeo × ~356 aberturas/h ≈ <b>23,6 MB/dia</b>. Recarregar o documento não alcança.</li>
 *   <li>Consequência observada: a RAM desceu até bater no backstop e, nas últimas 28h, o `reload_log`
 *   tinha <b>20 de 20 entradas com `reason: "ram"`</b> — uma a cada ~88min (~16 recargas/dia, ~74s de
 *   preto/dia). O backstop virou o ÚNICO gatilho e o operador relatou vídeo entrando atrasado, com o
 *   player correndo atrás do sincronismo.</li>
 * </ul>
 * A Opção A (recycle de planos) segue REPROVADA (curva de RAM desceu RETA através de 19+6 recycles);
 * "só o PROCESSO novo devolve o resíduo" continua valendo — o que mudou é QUEM troca o processo.
 *
 * <p>🔵 MUDANÇA 2026-10-14 — **na TIZEN o refresh reinicia o PROCESSO**, via o
 * `IPlatformService.restartProcess` (alarme + `exit()`), em vez de `location.reload()`. Gate por
 * CAPACIDADE (`typeof restartProcess === 'function'`) **E** por plataforma (`tizen`), atrás da flag de
 * env `VITE_RLD_PROCESS_RESTART` (default ON — kill-switch sem rebuild de código). Android, embedded e
 * browser seguem no `location.reload()`, sem nenhuma mudança: o Android TEM `restartProcess`, mas
 * ligá-lo aqui atingiria os 6000 tablets da frota de uma vez, sem validação. Sem a capacidade, com a
 * flag off, ou se o restart LANÇAR → cai no reload; o refresh nunca fica sem efeito. O caminho usado
 * fica gravado no `reload_log` (`mode: 'process' | 'page'`) pra dar pra verificar na telemetria.
 *
 * <p>GATILHO PRIMÁRIO = TEMPO, não RAM (decisão Renato 2026-07-27). A RAM livre depende de cada TV
 * (RAM total + o que o sistema roda) e OSCILA/estabiliza — key na RAM absoluta é imprevisível. O
 * tempo é agnóstico de modelo e PREVISÍVEL: a cada `RELOAD_INTERVAL_MS` (default 90min, configurável
 * por SW/env) recarrega e nunca chega na zona instável da morte (~1,7h medido). Controla o nº/dia:
 * 24h / intervalo (90min ≈ 16×/dia).
 *
 * <p>BACKSTOP = RAM (secundário). Se a RAM cair abaixo de `RAM_BACKSTOP_MB` (~200MB) ANTES do tempo
 * — TV que vaza mais rápido / RAM menor — recarrega antes. Rede de segurança pra outliers, não o
 * gatilho principal.
 *
 * <p>🔴 ÂNCORA = WRAP DO LOOP (último item → primeiro), e isso NÃO é negociável (Renato). O reload
 * SEMPRE acontece na volta do loop, INDEPENDENTE do tipo (img OU vídeo) do último e do primeiro item
 * — é o `playNext` com `nextIndex===0`, que é agnóstico de tipo. Nunca no meio de um item (uma
 * tentativa anterior ancorou no fim de CADA vídeo → preto no fim de todo vídeo, regressão visível).
 * Se o gatilho arma no meio do loop, a intenção FICA GUARDADA e espera o wrap.
 *
 * <p>LIGADO POR DEFAULT (`RLD_RAM_RELOAD` no changeSpy = true). Killable em runtime pelo override do
 * changeSpy. VALIDADO na bancada (2026-07-28): 6 reciclos por tempo numa noite, RAM sempre
 * recuperando (ex.: 439→792MB), err=0, zero crash.
 *
 * <p>🔵 MUDANÇA 2026-09-19 (Renato) — de "só build AVPlay a cada 60min" para **todas as plataformas
 * de dispositivo a cada 24h**. Duas razões, ambas medidas:
 * <ul>
 *   <li><b>Cobertura</b>: o gate antigo era `VITE_AVPLAY_BACKEND === 'true'`, sem check de
 *   plataforma; como o `generate-env.ts` injeta essa flag em todo build não-embedded, a frota
 *   Android recarregava de hora em hora por ACIDENTE, e o embedded (flag=false) não recarregava
 *   NUNCA. Agora o gate é explícito por plataforma — vide {@link RELOAD_PLATFORMS}.</li>
 *   <li><b>Cadência</b>: 60min escondia a acumulação. Medido no Tab A7, mesmo aparelho e mesmo loop
 *   de 9 slots, frota × embedded derivam IGUAL (+0,12 × +0,10 MB/min de VmSize) — a frota só parecia
 *   plana porque o reload devolvia ~74MB a cada ~58min. Em 24h a curva fica observável (diagnóstico)
 *   e o piso ainda volta antes de qualquer teto: 0,1 MB/min × 24h ≈ 144MB, contra ~4GB de pool.</li>
 * </ul>
 * O backstop de RAM continua sendo a rede de segurança pra TV que não aguentar as 24h — e ele é
 * naturalmente inerte fora da Tizen, porque `readNativeAvailMB()` lê `tizen.systeminfo` e retorna
 * `null` nas demais plataformas (lá só o gatilho de TEMPO age).
 *
 * <p>Intervalo configurável por SW via `VITE_RLD_INTERVAL_MIN` (minutos) — útil pra reproduzir em
 * bancada sem esperar um dia. Histórico da cadência antiga: 90min (2026-07-27) → 60min (2026-07-31,
 * após a bancada cair em ~72min com streams no loop, que aceleram o leak de CMA) → 24h (2026-09-19).
 */

/**
 * Intervalo do refresh periódico — gatilho PRIMÁRIO. Configurável por SW (env `VITE_RLD_INTERVAL_MIN`,
 * em minutos); default **1440min = 24h** (Renato 2026-09-19).
 *
 * <p>Era 60min, e nessa cadência o reload MASCARAVA a acumulação de memória em vez de deixá-la
 * observável: medido no Tab A7 lado a lado (mesmo aparelho, mesmo loop de 9 slots), frota e embedded
 * derivam IGUAL (+0,12 e +0,10 MB/min de VmSize); a frota só parecia estável porque o reload de 60min
 * devolvia ~74MB e zerava o piso a cada ~58min. Com 24h a acumulação fica visível por um dia inteiro
 * (diagnóstico) e ainda assim volta ao piso antes de qualquer teto — a 0,1 MB/min, 24h somam ~144MB,
 * bem longe dos ~4GB do pool do renderer 32-bit.
 */
const RELOAD_INTERVAL_MIN =
  Number(import.meta.env.VITE_RLD_INTERVAL_MIN) || 1440;
export const RELOAD_INTERVAL_MS = RELOAD_INTERVAL_MIN * 60_000;

/** Backstop de RAM (gatilho SECUNDÁRIO): TV que vaza rápido / RAM menor recarrega antes do tempo. */
export const RAM_BACKSTOP_MB = 200;

/**
 * Kill-switch do restart de PROCESSO na Tizen (`VITE_RLD_PROCESS_RESTART`) — **default ON**.
 *
 * <p>Só `'false'` desliga; qualquer outro valor (inclusive ausente) mantém ligado. Desligar volta ao
 * `location.reload()` de antes de 2026-10-14 — que não devolve memória nativa nesta TV, mas é o
 * comportamento conhecido e já rodado em campo. Existe pra dar reversão **sem rebuild de código**
 * (basta o env do build), já que a troca mexe no que acontece a cada 24h na frota de TVs.
 *
 * <p>Lida em RUNTIME (não em constante de módulo) de propósito: é o que deixa o teste trocar o valor
 * com `vi.stubEnv` depois do import, e o custo é uma leitura de objeto a cada wrap de loop.
 */
function isProcessRestartEnabled(): boolean {
  return import.meta.env.VITE_RLD_PROCESS_RESTART !== 'false';
}

/**
 * Piso de uptime antes de QUALQUER reload E intervalo mínimo entre reloads — anti-loop (TV
 * recarregando em ciclo numa loja se o boot nascer com pouca RAM). Escala com o intervalo pra
 * permitir teste com intervalo curto: `min(10min, intervalo/2)`.
 */
export const RELOAD_MIN_UPTIME_MS = Math.min(
  10 * 60_000,
  Math.floor(RELOAD_INTERVAL_MS / 2),
);

const RELOAD_LOG_KEY = 'efflux_avplay_reload_log_v1';
const RELOAD_LOG_MAX = 20;
const LAST_RELOAD_KEY = LAST_RELOAD_AT_KEY;

export interface ReloadLogEntry {
  ts: number;
  freeMB: number | null;
  opens: number | null;
  uptimeMs: number;
  bootId: string;
  /** Por que recarregou: `time` (intervalo periódico) ou `ram` (backstop). */
  reason?: ReloadReason;
  /**
   * COMO recarregou: `process` (restart de processo — Tizen) ou `page` (`location.reload()`).
   *
   * <p>**Opcional por back-compat**: entradas gravadas antes de 2026-10-14 não têm o campo, e o ring
   * atravessa reinstalação do app. Ausente ⇒ leia como `page` (era o único caminho que existia).
   *
   * <p>Significa "qual caminho foi ACIONADO", não "o processo de fato morreu": se nenhum alarme for
   * aceito, o próprio `TizenPlatformService.restartProcess` cai no reload por dentro e registra isso
   * em `efflux_relaunch_alarm_v1` (`scheduled: false`) — é lá que se confirma a troca de processo.
   */
  mode?: ReloadMode;
}

export type ReloadReason = 'time' | 'ram';

export type ReloadMode = 'process' | 'page';

/**
 * A decisão, pura e testável. Gatilho PRIMÁRIO = tempo (intervalo periódico); SECUNDÁRIO = backstop
 * de RAM. Sempre gated pelas guardas (âncora no wrap + anti-loop). `reason=null` = não recarrega.
 */
export function shouldReload(p: {
  /** RAM livre em MB. `null` = não conseguimos medir → só o gatilho de TEMPO age. */
  freeMB: number | null;
  /** Estamos no wrap do loop (último→primeiro)? Fora dele a resposta é sempre `null`. */
  atLoopWrap: boolean;
  /** Uptime desta sessão. */
  uptimeMs: number;
  /** Quando foi o último reload nosso (persistido entre sessões). */
  lastReloadAt: number | null;
  now: number;
  intervalMs?: number;
  backstopMB?: number;
  minUptimeMs?: number;
}): ReloadReason | null {
  const interval = p.intervalMs ?? RELOAD_INTERVAL_MS;
  const backstop = p.backstopMB ?? RAM_BACKSTOP_MB;
  const minUptime = p.minUptimeMs ?? RELOAD_MIN_UPTIME_MS;
  if (!p.atLoopWrap) return null; // âncora: só no wrap (último→primeiro), nunca no meio
  if (p.uptimeMs < minUptime) return null; // anti-loop (boot recente)
  if (p.lastReloadAt != null && p.now - p.lastReloadAt < minUptime) {
    return null; // anti-loop (recarregou agorinha)
  }
  if (p.uptimeMs >= interval) return 'time'; // PRIMÁRIO: passou o intervalo
  if (p.freeMB != null && p.freeMB < backstop) return 'ram'; // BACKSTOP: RAM baixa antes do tempo
  return null;
}

/**
 * Lê `opens` da chave que o `tizenAvplay` já persiste. De propósito por localStorage e não por
 * import: importar o `tizenAvplay` a partir do store puxaria o módulo do AVPlay pro bundle em
 * TODA plataforma, mesmo sem `VITE_AVPLAY_BACKEND=true` — e a validação de build depende
 * justamente desse tree-shaking. Mesmo bridge que o `crashCapture` usa.
 */
function readOpens(): number | null {
  try {
    const raw = window.localStorage?.getItem('efflux_avplay_counters_v1');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { opens?: unknown };
    return typeof parsed.opens === 'number' ? parsed.opens : null;
  } catch {
    return null;
  }
}

function readLastReloadAt(): number | null {
  try {
    const raw = window.localStorage?.getItem(LAST_RELOAD_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function appendReloadLog(entry: ReloadLogEntry): void {
  try {
    const raw = window.localStorage?.getItem(RELOAD_LOG_KEY);
    const list: ReloadLogEntry[] = raw
      ? (JSON.parse(raw) as ReloadLogEntry[])
      : [];
    list.push(entry);
    window.localStorage?.setItem(
      RELOAD_LOG_KEY,
      JSON.stringify(list.slice(-RELOAD_LOG_MAX)),
    );
    window.localStorage?.setItem(LAST_RELOAD_KEY, String(entry.ts));
  } catch {
    /* best-effort */
  }
}

/**
 * Corrige o `mode` da ÚLTIMA entrada do ring.
 *
 * <p>O registro tem que ser gravado ANTES de acionar o refresh (depois do `exit()` não existe mais
 * quem grave), então ele guarda a INTENÇÃO. Quando o restart de processo lança e caímos no reload, a
 * intenção vira mentira — e um `reload_log` que mente sobre o caminho não serve pra verificar se a
 * mudança pegou, que é a razão de o campo existir. Só o último elemento pode estar errado: o
 * single-flight garante uma decisão por processo.
 */
function patchLastReloadLogMode(mode: ReloadMode): void {
  try {
    const raw = window.localStorage?.getItem(RELOAD_LOG_KEY);
    if (!raw) return;
    const list = JSON.parse(raw) as ReloadLogEntry[];
    if (!Array.isArray(list) || list.length === 0) return;
    list[list.length - 1] = { ...list[list.length - 1], mode };
    window.localStorage?.setItem(RELOAD_LOG_KEY, JSON.stringify(list));
  } catch {
    /* best-effort */
  }
}

/** Histórico de reloads (diagnóstico/testes). */
export function getReloadLog(): ReloadLogEntry[] {
  try {
    const raw = window.localStorage?.getItem(RELOAD_LOG_KEY);
    return raw ? (JSON.parse(raw) as ReloadLogEntry[]) : [];
  } catch {
    return [];
  }
}

/** Single-flight: uma vez decidido recarregar, ignora tudo até o processo morrer. */
let reloading = false;
const sessionStartedAt = Date.now();

/**
 * Plataformas de DISPOSITIVO que recebem o refresh periódico: as que rodam 24/7 sem ninguém
 * reiniciando o app. `browser` e `ios` ficam de fora (sessão tem dono humano, que fecha a aba).
 */
const RELOAD_PLATFORMS: ReadonlySet<Platform> = new Set<Platform>([
  'android',
  'tizen',
  'webos',
  'embedded',
]);

/**
 * Gate de PLATAFORMA (Renato 2026-09-19) — era gate de BUILD (`VITE_AVPLAY_BACKEND === 'true'`).
 *
 * <p>O gate antigo não checava plataforma nenhuma, e como o `generate-env.ts` injeta
 * `VITE_AVPLAY_BACKEND=true` em TODO build que não é embedded, a frota Android vinha recarregando de
 * hora em hora **por acidente** — ninguém pediu isso pro Android, era pra ser só Tizen (confirmado em
 * campo: 2 devices de loja, 20 entradas cada, 100% `reason='time'`, ~61-63min). Pior: a estabilidade
 * de memória da frota dependia inteiramente desse efeito colateral, então "consertar" o gate sem mais
 * nada teria feito a frota inteira passar a se comportar como o embedded.
 *
 * <p>Agora é explícito: todas as plataformas de dispositivo recebem o reload, inclusive o
 * **embedded** — que não tinha nenhum (o `generate-env.ts` seta `VITE_AVPLAY_BACKEND=false` lá) e era
 * o único a acumular sem nunca zerar. Isso também atende o requisito de manter o embedded o mais
 * idêntico possível à frota.
 *
 * <p>Detecção em RUNTIME (não build-time): o reload deixou de ser uma peculiaridade do AVPlay e virou
 * comportamento de produto, então o corpo não é mais tree-shaken fora da Tizen — é intencional.
 * Chamado DENTRO da função (nunca em constante de módulo): `detectPlatform` lê `window`, e no import
 * do módulo o host ainda não injetou os marcadores.
 */
function isReloadPlatform(): boolean {
  return RELOAD_PLATFORMS.has(detectPlatform());
}

/**
 * Resolve COMO este refresh vai acontecer: restart de PROCESSO ou recarga de página.
 *
 * <p>Retorna o thunk do restart quando as TRÊS condições valem, e `null` (⇒ reload) em qualquer
 * outro caso:
 * <ol>
 *   <li><b>Flag ligada</b> — `VITE_RLD_PROCESS_RESTART !== 'false'` (kill-switch, default ON).</li>
 *   <li><b>Plataforma é TIZEN</b> — explícito, e não só "tem a capacidade". O Android TAMBÉM
 *   implementa `restartProcess` (trampolim em processo separado), mas ligá-lo aqui mudaria o
 *   comportamento dos 6000 tablets da frota de uma vez, sem validação — está fora desta mudança.</li>
 *   <li><b>A capacidade existe</b> — gate por `typeof === 'function'`, do mesmo jeito que o
 *   `app:restart` do RemoteOps: a ausência é a própria resposta "aqui não dá", sem lista de nomes e
 *   sem caso especial pra build antigo.</li>
 * </ol>
 *
 * <p>O thunk CHAMA o método no objeto (`plataforma.restartProcess()`) em vez de extrair a função: o
 * `TizenPlatformService` usa `this` lá dentro (`isNativeEnvironment`, `agendarRelaunch`).
 *
 * @param dep substitui a capacidade da plataforma (teste). `null` = "plataforma sem a capacidade";
 *   `undefined` = usa a real. O gate de flag/plataforma vale IGUAL com o dep — senão o teste não
 *   conseguiria provar que Android/browser continuam no reload.
 */
function resolveProcessRestart(dep?: (() => void) | null): (() => void) | null {
  if (!isProcessRestartEnabled()) return null;
  if (detectPlatform() !== 'tizen') return null;
  if (dep !== undefined) return dep;
  const plataforma = window.__effluxPlatform;
  if (typeof plataforma?.restartProcess !== 'function') return null;
  return () => plataforma.restartProcess!();
}

/**
 * Chamar NO WRAP DO LOOP. Avalia (tempo ≥ intervalo OU backstop de RAM) e, se for o caso, marca o
 * recycle-boot (pra o cliente ver só preto — vide `recycleBoot`) e faz o refresh.
 *
 * <p>O refresh é restart de PROCESSO na Tizen e recarga de PÁGINA nas demais — vide
 * {@link resolveProcessRestart}. Pro chamador não muda nada: nos dois casos esta página está morrendo
 * e o índice pertence ao boot seguinte.
 *
 * @param deps injetáveis pra teste (sem eles, usa as fontes reais)
 * @returns `true` se disparou o refresh (processo ou página)
 */
export function maybeReloadAtLoopWrap(deps?: {
  freeMB?: number | null;
  opens?: number | null;
  now?: number;
  reload?: () => void;
  /**
   * Substitui a capacidade de restart da plataforma (teste). `null` = plataforma SEM a capacidade.
   * Continua passando pelo gate de flag + Tizen — vide {@link resolveProcessRestart}.
   */
  restartProcess?: (() => void) | null;
}): boolean {
  if (!isReloadPlatform()) return false; // só em plataforma de dispositivo (não browser/ios)
  if (!isChangeOn('RLD_RAM_RELOAD')) return false; // kill-switch (default ON)
  // 🔴 `true`, NÃO `false` (bug medido em campo na SSSP, 2026-07-28). O retorno significa "NÃO
  // avance o índice", não "eu disparei o reload agora". Um reload já em voo é o motivo MAIS forte
  // pra não avançar: a página está morrendo e o índice pertence ao boot seguinte.
  //
  // Com `false`, a 1ª chamada segurava o índice mas a 2ª (a mídia não trocou, então algo chama
  // `handleMediaEnd` de novo dentro da janela do reload) caía aqui, o caller avançava e o React
  // pintava o PRIMEIRO conteúdo do loop — o "verde → preto → verde" observado na TV, com o último
  // item vermelho e o primeiro verde. Além do visual, aquele slot fantasma emitia `slot_started` de
  // conteúdo que NUNCA tocou, inflando a exibição do 1º item a cada reciclagem (R1, mínimos
  // contratuais). Coberto por teste — vide `ramReload.test.ts`.
  if (reloading) return true; // single-flight — segura o índice até a página ir embora

  const now = deps?.now ?? Date.now();
  const freeMB = deps?.freeMB !== undefined ? deps.freeMB : readNativeAvailMB();
  const uptimeMs = now - sessionStartedAt;
  const lastReloadAt = readLastReloadAt();

  const reason = shouldReload({
    freeMB,
    atLoopWrap: true,
    uptimeMs,
    lastReloadAt,
    now,
  });
  if (!reason) return false;

  reloading = true;
  // Decidido ANTES de agir porque o registro tem que sair antes: depois do `exit()` não existe mais
  // quem grave. Vide `patchLastReloadLogMode` pro caso em que a intenção não se concretiza.
  const restartProcess = resolveProcessRestart(deps?.restartProcess);
  const mode: ReloadMode = restartProcess ? 'process' : 'page';
  noteChangeAction(
    'RLD_RAM_RELOAD',
    `${reason} ${mode} ram=${freeMB}MB up=${Math.round(uptimeMs / 60000)}min`,
  );
  appendReloadLog({
    ts: now,
    freeMB,
    opens: deps?.opens !== undefined ? deps.opens : readOpens(),
    uptimeMs,
    bootId: getBootId(),
    reason,
    mode,
  });
  // Marca ANTES de recarregar: é o que faz o próximo boot renderizar preto puro em vez da UI
  // de sistema. Tem teto de segurança embutido (`RECYCLE_BOOT_CAP_MS`).
  markRecycleBoot(now);
  // ...e pinta preto JÁ, nesta página. `markRecycleBoot` só cobre o documento NOVO, e entre os dois
  // os pixels da página velha seguem na tela. Quando o último item do loop é `<img>` não há nada
  // pra apagar (o teardown de mídia só mexe em `<video>`), então a imagem ficava no ar até o
  // documento novo pintar. Vide `paintBlackNow`.
  paintBlackNow(now);
  const doReload = deps?.reload ?? ((): void => window.location.reload());
  if (restartProcess) {
    try {
      restartProcess();
      return true;
    } catch (err) {
      // O refresh NUNCA pode ficar sem efeito: se a troca de processo falha, a recarga de página
      // ainda é o comportamento antigo — não devolve memória nativa, mas a loja segue tocando.
      console.warn(
        '[RLD] restartProcess falhou, caindo pro reload de página:',
        err,
      );
      patchLastReloadLogMode('page');
    }
  }
  doReload();
  return true;
}

/** Só pra teste — zera o single-flight entre casos. */
export function __resetReloadingForTest(): void {
  reloading = false;
}
